import { useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";

import { useFieldMutation } from "../features/field-values/fieldValueApiSlice";
import { isRealLoanGuid } from "../utils/normalize-xml-for-create-loan";
import {
	AURA_LOCK_RECHECK_INTERVAL_MS,
	AURA_RESULT_IN_PROGRESS,
	AURA_STALE_IN_PROGRESS_MINUTES,
	ENCOMPASS_FIELD_IDS,
} from "../utils/aura.constants";

const REQUEST_DT_FORMAT = "MM-dd-yyyy hh:mm a";
const EASTERN_ZONE = "America/New_York";

// { FIELD_ID_UPPER: value } from a field_values response (Encompass upper-cases CX.* ids).
export const fieldValueMap = (res) =>
	Object.fromEntries(
		(res?.response || []).map((item) => [String(item?.fieldId || "").toUpperCase(), item?.value])
	);

// True while an AURA run is in flight: CX.AUS.RESULT is "In Progress" and the run started less than
// AURA_STALE_IN_PROGRESS_MINUTES ago. A missing or unreadable start time counts as stale (not locked),
// the same rule as the backend's is_in_progress_stale.
export const isAuraRunLocked = (result, requestDt, now = DateTime.now()) => {
	if (String(result || "").trim().toLowerCase() !== AURA_RESULT_IN_PROGRESS.toLowerCase()) {
		return false;
	}
	const started = DateTime.fromFormat(String(requestDt || "").trim(), REQUEST_DT_FORMAT, {
		zone: EASTERN_ZONE,
	});
	if (!started.isValid) return false;
	return now.diff(started, "minutes").minutes < AURA_STALE_IN_PROGRESS_MINUTES;
};

// Reads CX.AUS.RESULT / CX.AUS.REQUEST.DT for the loan and reports whether current-loan edits must
// be locked. While locked it re-checks every AURA_LOCK_RECHECK_INTERVAL_MS so the page unlocks by itself.
// A failed read leaves the page unlocked (the backend guard still rejects writes with a 409).
const useAuraInProgress = (loanGuid) => {
	const [readFields] = useFieldMutation();
	const [locked, setLocked] = useState(false);
	const [checking, setChecking] = useState(false);
	const mountedRef = useRef(true);

	const recheck = useCallback(async () => {
		if (!isRealLoanGuid(loanGuid)) {
			setLocked(false);
			return false;
		}
		setChecking(true);
		let next = false;
		try {
			const res = await readFields({
				loan_guid: loanGuid,
				fields: [ENCOMPASS_FIELD_IDS.AUS_RESULT, ENCOMPASS_FIELD_IDS.AUS_REQUEST_DT].join(","),
			}).unwrap();
			const values = fieldValueMap(res);
			next = isAuraRunLocked(
				values[ENCOMPASS_FIELD_IDS.AUS_RESULT.toUpperCase()],
				values[ENCOMPASS_FIELD_IDS.AUS_REQUEST_DT.toUpperCase()]
			);
		} catch (err) {
			next = false;
		}
		if (mountedRef.current) {
			setLocked(next);
			setChecking(false);
		}
		return next;
	}, [loanGuid, readFields]);

	useEffect(() => {
		mountedRef.current = true;
		recheck();
		return () => {
			mountedRef.current = false;
		};
	}, [recheck]);

	// Re-check on an interval only while locked, so the page unlocks by itself.
	useEffect(() => {
		if (!locked) return undefined;
		const timer = setInterval(recheck, AURA_LOCK_RECHECK_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [locked, recheck]);

	return { locked, checking, recheck };
};

export default useAuraInProgress;
